<template>
  <section class="landing">
    <header class="landing-head">
      <p class="eyebrow">Centerfire Services</p>
      <h1>{{ heading }}</h1>
      <p v-if="tag" class="tag">CFM# {{ tag }}</p>
      <p class="lede">This machine is on record with Centerfire. Choose what you need.</p>
    </header>

    <div v-if="!sheet && !gateOpen && !requestOpen" class="choices">
      <button class="choice" type="button" @click="openGate">
        <strong>Service and repair history</strong>
        <span>Everything we have done to this machine. Print it or save it as a PDF.</span>
      </button>
      <button class="choice" type="button" @click="openRequest">
        <strong>Request service on this machine</strong>
        <span>Tell us what it is doing and we will get back to you.</span>
      </button>
      <router-link class="choice" :to="plansTo">
        <strong>Explore monthly maintenance plans</strong>
        <span>What a monthly visit covers, and what it would cost for this machine.</span>
      </router-link>
    </div>

    <section v-if="requestOpen" class="gate">
      <h2>Request service</h2>
      <p v-if="!requestSent">Tell us what the machine is doing. No PIN needed. Leave a name and number if you would like us to call back.</p>
      <template v-if="!requestSent">
        <form @submit.prevent="sendRequest">
          <label class="wide">What is it doing?<textarea v-model.trim="request.message" required maxlength="4000" rows="4" placeholder="Leaking hydraulic fluid at the left arm"></textarea></label>
          <label>Your name (optional)<input v-model.trim="request.name" maxlength="120" autocomplete="name" /></label>
          <label>Phone (optional)<input v-model.trim="request.phone" maxlength="40" inputmode="tel" autocomplete="tel" /></label>
          <label class="trap" aria-hidden="true"><input v-model="request.company" tabindex="-1" autocomplete="off" /></label>
          <button class="btn btn-primary" :disabled="busy || !request.message">{{ busy ? 'Sending…' : 'Send request' }}</button>
        </form>
        <p v-if="error" class="status error" role="status">{{ error }}</p>
        <button class="linkish" type="button" @click="closeRequest">Back</button>
      </template>
      <template v-else>
        <p role="status">Thank you — we have it. Centerfire will get back to you.</p>
        <button class="linkish" type="button" @click="closeRequest">Back</button>
      </template>
    </section>

    <section v-if="gateOpen && !sheet" class="gate">
      <h2>Service history</h2>
      <p>Enter the customer PIN Centerfire gave you. It is on your invoice, or we can text it to you.</p>
      <form @submit.prevent="unlock">
        <label>Customer PIN<input v-model.trim="pin" type="password" inputmode="numeric" autocomplete="current-password" required autofocus /></label>
        <button class="btn btn-primary" :disabled="busy">{{ busy ? 'Opening…' : 'Open history' }}</button>
      </form>
      <p v-if="error" class="status error" role="status">{{ error }}</p>
      <button class="linkish" type="button" @click="closeGate">Back</button>
    </section>

    <HistorySheet
      v-if="sheet"
      ref="historySheet"
      :identity="sheet.identity"
      :current-hours="sheet.currentReading?.value ?? null"
      :meter-unit="sheet.currentReading?.unit === 'miles' ? 'miles' : 'hours'"
      :rows="sheet.history"
      :owner-greasings="sheet.ownerGreasings"
      @close="sheet = null"
    />
  </section>
</template>

<script>
import HistorySheet from '@/components/HistorySheet.vue';
import { loadHistorySheet, requestServiceOnMachine, unlockPortal } from '@/data/maintainedUnits';

/**
 * What a scan shows anyone who is not an enrolled program customer: a one-off customer, or a
 * passerby who scanned a tag in a yard.
 *
 * Machine-centric by design. Nothing here names the owner before or after the PIN — the header
 * is the machine, and the history sheet behind the gate carries no customer identity either.
 *
 * A gate opened here can also turn out to belong to a program customer (an untagged machine
 * reached by token link, where the client cannot classify ahead of time). The unlock response
 * says which, and a fleet-scoped session is handed back up so the full portal opens instead.
 */
export default {
  name: 'BasicMachineLanding',
  components: { HistorySheet },
  props: {
    // The tag or machine token the visitor arrived on; what the unlock and history calls use.
    scanKey: { type: String, required: true },
    byToken: { type: Boolean, default: false },
    tag: { type: String, default: '' },
    label: { type: String, default: '' },
    publicType: { type: String, default: '' },
    // A sheet already fetched by the parent — when a history-scoped session was opened on the
    // view's own PIN card, which cannot render the sheet itself. Closing it lands on the three
    // choices, the same as unlocking here would.
    initialSheet: { type: Object, default: null },
  },
  emits: ['fleet-session'],
  data() {
    return { gateOpen: false, requestOpen: false, requestSent: false, busy: false, pin: '', error: '', sheet: this.initialSheet, request: { message: '', name: '', phone: '', company: '' } };
  },
  computed: {
    heading() { return this.label || this.publicType || 'Machine record'; },
    plansTo() { return { path: '/managed-maintenance-explained', query: { machine: this.scanKey } }; },
  },
  methods: {
    openGate() { this.gateOpen = true; this.error = ''; },
    closeGate() { this.gateOpen = false; this.pin = ''; this.error = ''; },
    openRequest() { this.requestOpen = true; this.requestSent = false; this.error = ''; },
    closeRequest() { this.requestOpen = false; this.requestSent = false; this.error = ''; this.request = { message: '', name: '', phone: '', company: '' }; },
    async sendRequest() {
      this.busy = true;
      this.error = '';
      const result = await requestServiceOnMachine({ key: this.scanKey, ...this.request });
      this.busy = false;
      if (result.error) { this.error = result.error; return; }
      this.requestSent = true;
    },
    async unlock() {
      this.busy = true;
      this.error = '';
      const credentials = this.byToken ? { machineToken: this.scanKey } : { tag: this.scanKey };
      const result = await unlockPortal({ ...credentials, pin: this.pin });
      this.pin = '';
      if (result.error) { this.error = result.error; this.busy = false; return; }
      if (result.scope === 'fleet') { this.busy = false; this.$emit('fleet-session'); return; }
      const record = await loadHistorySheet(this.scanKey);
      this.busy = false;
      if (record.error) { this.error = record.error; return; }
      this.sheet = record.sheet;
      this.gateOpen = false;
      this.$nextTick(() => this.$refs.historySheet?.focus());
    },
  },
};
</script>

<style scoped>
.landing { position: relative; display: grid; gap: 2rem; }
.landing-head .eyebrow { margin: 0; color: var(--orange); font-size: .78rem; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
.landing-head h1 { margin: .4rem 0; font-size: clamp(1.9rem,5vw,3.2rem); font-weight: 500; }
.landing-head .tag { margin: 0; color: #bbb; font-weight: 600; }
.landing-head .lede { margin: .8rem 0 0; color: #bbb; max-width: 46ch; }
.choices { display: grid; gap: 1px; background: #333; border: 1px solid #333; }
.choice { display: grid; gap: .35rem; padding: 1.4rem; border: 0; background: #111; color: inherit; font: inherit; text-align: left; text-decoration: none; cursor: pointer; }
.choice:hover,.choice:focus-visible { background: #1b1b1b; }
.choice strong { font-size: 1.1rem; }
.choice span { color: #bbb; font-size: .9rem; }
.gate { padding: clamp(1.5rem,4vw,2.5rem); background: #111; border: 1px solid #333; }
.gate h2 { margin: 0 0 .5rem; font-weight: 500; }
.gate p { color: #bbb; }
.gate form { display: grid; gap: 1rem; margin: 1.5rem 0 0; grid-template-columns: 1fr auto; align-items: end; }
.gate form:has(.wide) { grid-template-columns: repeat(2,minmax(0,1fr)); }
.gate .wide { grid-column: 1 / -1; }
.gate textarea { padding: .8rem; border: 1px solid #444; background: #1b1b1b; color: inherit; font: inherit; resize: vertical; }
/* Honeypot: off-screen rather than display:none, which some bots skip. */
.trap { position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden; }
.gate label { display: grid; gap: .4rem; font-size: .8rem; letter-spacing: .04em; text-transform: uppercase; }
.gate input { padding: .8rem; border: 1px solid #444; background: #1b1b1b; color: inherit; font: inherit; }
.status.error { color: #ff9a6a; }
.linkish { margin-top: 1rem; padding: 0; border: 0; background: none; color: var(--orange); font: inherit; text-decoration: underline; cursor: pointer; }

@media (max-width: 760px) { .gate form { grid-template-columns: 1fr; } }

@media print {
  .landing-head,.choices,.gate { display: none !important; }
}
</style>
